import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BaseModal } from './BaseModal';
import { publicIdSelector, submitMarketingConsentThunk, submittingMarketingConsentSelector, userSelector } from '_app/features/session';

export const MarketingConsentModal = () => {
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const publicId = useSelector(publicIdSelector);
  const isSubmitting = useSelector(submittingMarketingConsentSelector);

  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [isClosed, setIsClosed] = useState(false);

  // Update phone when user data loads
  useEffect(() => {
    if (user.phone) {
      setPhone(user.phone);
    }
  }, [user.phone]);

  // Only show if marketing_consent is explicitly false AND user hasn't closed it
  const shouldShow = user.marketing_consent === false && !isClosed;

  const handleClose = () => {
    setIsClosed(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!phone || !consent) {
      setError('Please fill in all required fields.');
      return;
    }

    dispatch(
      submitMarketingConsentThunk({
        phone,
        marketing_consent: consent,
        publicId,
      }),
    )
      .unwrap()
      .catch((submitError) => {
        setError(submitError?.message || submitError || 'An error occurred. Please try again.');
      });
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <BaseModal
      visible={shouldShow}
      overlayClassName="!bg-opacity-50 backdrop-blur-sm !flex !items-center !justify-center"
      className="bg-white rounded-lg shadow-2xl w-[95%] md:max-w-lg m-4 outline-none overflow-y-auto max-h-[90vh]"
      style={{ height: 'fit-content' }}
      onClose={handleClose}
    >
      <div className="p-6 md:p-8">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Unlock Hidden &amp; Off-Market Homes</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-gray-600 mb-6">
          Get instant access to properties you <em>can’t find on Zillow or HAR.com</em>.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="consent-phone" className="block text-gray-700 text-sm font-semibold mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="consent-phone"
              name="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="(555) 555-5555"
              required
            />
          </div>

          <div className="mb-6">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                id="consent-checkbox"
                name="marketing_consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
                required
              />
              <span className="ml-3 text-xs text-gray-600 leading-relaxed">
                By opting in, you’ll get early access to exclusive off-market and coming-soon listings before they’re public. I consent to receive
                phone calls and text messages from HoustonProperties.com. These may include marketing messages sent using an automatic telephone
                dialing system and artificial or prerecorded voices. Consent is not required to purchase goods or services. Message and data rates may
                apply. I may opt out at any time by replying STOP to texts or contacting HoustonProperties.com.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-4 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isSubmitting ? 'Submitting...' : 'Show Me Hidden Listings'}
          </button>

          {error && <div className="mt-4 text-red-600 text-sm">{error}</div>}
        </form>
      </div>
    </BaseModal>
  );
};
